import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Autoplay } from 'swiper';
import 'swiper/css';
import 'swiper/css/free-mode';
import LazyVideo from './LazyVideo';
import BirdsEyeView from '../assets/img/about/BirdsEyeView.mp4';
import IngredientTracking from '../assets/img/about/IngredientTracking.mp4';
import SpotlightComponent from './Spotlight';
import GBP from '../assets/img/about/GBP.mp4';
import SMSManagement from '../assets/img/about/SMSManagement.mp4';
import Scheduling from '../assets/img/about/Scheduling.mp4';
import GuestNotes from '../assets/img/about/GuestNotes.mp4';
import PreOrdering from '../assets/img/about/PreOrdering.mp4';

const demos = [
  {
    id: 1,
    video: GBP,
    description: 'Update Your Google Business Profile From the Same App You Seat Guests',
    summary:
      'No other POS system on the market includes Google Business Profile integration at any price tier. Connect your Google Business account once and you can update your profile and hours, post featured menu items directly to Google Search, and see exactly how many people searched for your restaurant, clicked your menu, or asked for directions — all from the GoHost portal. The equivalent of a dedicated marketing tool built into your POS, at no extra cost.',
  },
  {
    id: 2,
    video: Scheduling,
    description: 'Publish Interactive Employee Shifts with Email Notifications — Your Team Will Love the Calendar Marketplace',
    summary:
      'Build your weekly schedule in the portal, hit publish, and every employee instantly receives a personalized email with their shifts. Staff can claim open shifts, request swaps, or drop shifts from their own portal login — managers approve with one tap. When someone clocks in, their floor zone is automatically assigned from the published shift. No more group message threads, no paper schedules in the back. Scheduling tools like 7shifts and HotSchedules charge $17–$150/month for a fraction of this.',
  },
  {
    id: 3,
    video: SMSManagement,
    description: 'Your Guests Get the Right Text at the Right Time, With the Ability to Respond',
    summary:
      'GoHost sends an array of automated messages — reservation confirmation, day-before reminder, auto-drop notice, waitlist join, wait time update, table-ready alert, preorder link, and schedule notification — all fully customizable with your own wording and dynamic tokens. Guests can reply YES to confirm or UNRESERVE to cancel, and the system handles everything automatically. Toast charges $75/month for SMS that only sends. GoHost\'s two-way SMS engine is included.',
  },
  {
    id: 4,
    video: BirdsEyeView,
    description: 'A Birds-Eye View of Every Reservation Across Your Floor, for Any Date',
    summary:
      'GoHost\'s reservation system shows you every booking across your floor in real time. Conflict detection prevents double-bookings before they happen and availabilites are mapped out for each table. All meal and payment statuses integrate directly with your live floor map so hosts always know exactly where each sitting stands in the dining lifecycle. Comeptitors charge for this visibility, while GoHost sees it as a necesity.',
  },
  {
    id: 5,
    video: IngredientTracking,
    description: 'Ingredient Level Inventory Tracking',
    summary:
      'GoHost tracks inventory down to the individual ingredient, not just the menu item. When a pickup order is accepted, ingredients are automatically deducted in real time — and when something hits zero, your manager gets ',
  },
    {
    id: 6,
    video: GuestNotes,
    description: 'Guest Notes, Preferences and Returning Guest Recognition',
    summary:
      'Every guest who books a reservation or joins the waitlist builds a history in GoHost. Staff can attach notes to a customer record/number — dietary restrictions, seating preferences, VIP status, anything — and those notes surface automatically the next time that guest books. Platforms that charge you per-cover for reservations often dont even offer this. GoHost includes it because knowing your guests is the whole point.',
  },
      {
    id: 7,
    video: PreOrdering,
    description: 'PreOrdering and Customer Engagemnt',
    summary:
      'If PreOrdering is enabled- guests who book online can order from your menu before arriving — their order is sent to the kitchen the moment they sit down. This unique engagemnt is another way Gohost empowers the customer to personalize their experince without any added work from restaurant staff.',
  },
  
];

// ── Individual slide — local state sequences the two-phase transition ─────────
// Phase: outgoing item vanishes instantly (DOM removal), 40ms later incoming
// item grows in from height 0. No AnimatePresence overlap, no snap.
const DemoSlide = ({ demo, isExpanded, onExpand, onClose }) => {
  const [showVideo,   setShowVideo]   = React.useState(true);
  const [showSummary, setShowSummary] = React.useState(false);

  React.useEffect(() => {
    let t;
    if (isExpanded) {
      setShowVideo(false);                           // video vanishes instantly
      t = setTimeout(() => setShowSummary(true), 40); // summary grows in after gap
    } else {
      setShowSummary(false);                         // summary vanishes instantly
      t = setTimeout(() => setShowVideo(true), 40);  // video grows in after gap
    }
    return () => clearTimeout(t);
  }, [isExpanded]);

  return (
  <div
    className="flex flex-col"
    style={{ perspective: '1100px', cursor: isExpanded ? 'pointer' : 'default' }}
    onClick={() => { if (isExpanded) onClose(); }}
  >

    {/* ── Video — maxHeight avoids height:auto measurement race with LazyVideo ── */}
    {showVideo && (
      <motion.div
        initial={{ maxHeight: 0, opacity: 0 }}
        animate={{ maxHeight: 900, opacity: 1 }}
        transition={{ duration: 0.44, ease: 'easeOut' }}
        style={{ overflow: 'hidden' }}
      >
        <LazyVideo
          src={demo.video}
          className="w-full min-w-[60vw] md:min-w-0 rounded-md drop-shadow-[0_12px_12px_rgba(249,249,141,0.5)]"
        />
      </motion.div>
    )}

    {/* ── Summary — same maxHeight technique ── */}
    {showSummary && (
      <motion.div
        initial={{ maxHeight: 0, opacity: 0 }}
        animate={{ maxHeight: 900, opacity: 1 }}
        transition={{ duration: 0.44, ease: 'easeOut' }}
        style={{ overflow: 'hidden' }}
      >
        <div
          className="rounded-md"
          style={{
            background: 'linear-gradient(155deg, rgba(22,22,28,0.99) 0%, rgba(14,14,20,1) 100%)',
            border: '1px solid rgba(249,249,141,0.22)',
            boxShadow: '0 12px 28px rgba(249,249,141,0.1)',
            padding: '22px 20px',
          }}
        >
          <p style={{ color: 'rgba(255,255,255,0.95)', fontSize: '0.875rem', lineHeight: '1.68', textAlign: 'center' }}>
            {demo.summary}
          </p>
        </div>
      </motion.div>
    )}

    {/* ── Description + button — no box, just floats below ── */}
    <div
      className="mt-4 flex flex-col items-center select-none"
      onClick={(e) => e.stopPropagation()}
      style={{ gap: '10px' }}
    >
      <p className="text-white text-md text-center leading-snug px-1">{demo.description}</p>

      <motion.button
        onClick={(e) => { e.stopPropagation(); isExpanded ? onClose() : onExpand(); }}
        whileTap={{ scale: 0.94 }}
        className="text-xs font-semibold rounded-full px-4 py-1.5"
        style={{
          background: isExpanded ? 'rgba(255,255,255,0.1)' : '#fe7634',
          color: 'white',
          border: isExpanded ? '1px solid rgba(255,255,255,0.22)' : '1px solid transparent',
        }}
      >
        {isExpanded ? 'Close' : 'Read More'}
      </motion.button>
    </div>

  </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────────
const Demonstrate = () => {
  const swiperRef  = useRef(null);
  const [expandedId, setExpandedId] = useState(null);

  const handleExpand = (id) => {
    setExpandedId(id);
    swiperRef.current?.autoplay?.stop();
  };

  const handleClose = React.useCallback(() => {
    setExpandedId(null);
    swiperRef.current?.autoplay?.start();
  }, []);

  // Close on any click outside the expanded slide
  React.useEffect(() => {
    if (expandedId === null) return;
    const handler = () => handleClose();
    const t = setTimeout(() => document.addEventListener('click', handler, { once: true }), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', handler);
    };
  }, [expandedId, handleClose]);

  return (
    <div className="relative">
      <SpotlightComponent offset={0} size={150} className="select-none" />
      <section className="bg-neutral-700 py-8 select-none">

        <div className="text-center mb-8 mt-[80px]">
          <p className="mb-4 text-sm uppercase tracking-widest text-[#f9f98d] drop-shadow-[0_5px_8px_#f9f98d]">
            Light up Your Crowd
          </p>
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="h2 font-bold text-white drop-shadow-[0_12px_14px_rgba(249,249,141,0.8)]"
          >
            Spotlight POS App Features
          </motion.h2>
        </div>

        <Swiper
          modules={[FreeMode, Autoplay]}
          freeMode={true}
          grabCursor={true}
          loop={true}
          spaceBetween={20}
          autoplay={{ delay: 0, disableOnInteraction: false }}
          speed={20000}
          freeModeMomentum={false}
          breakpoints={{
            0:   { slidesPerView: 1.5 },
            768: { slidesPerView: 2.5 },
          }}
          onSwiper={(swiper) => { swiperRef.current = swiper; }}
        >
          {demos.map((demo) => (
            <SwiperSlide key={demo.id} className="flex flex-col items-center">
              <DemoSlide
                demo={demo}
                isExpanded={expandedId === demo.id}
                onExpand={() => handleExpand(demo.id)}
                onClose={handleClose}
              />
            </SwiperSlide>
          ))}
        </Swiper>

      </section>
    </div>
  );
};

export default Demonstrate;
